import type { RouteLocationNamedRaw, RouteLocationNormalizedLoaded } from 'vue-router'

export type LibrarySubKey = 'checklist' | 'templates' | 'default-checklist'
export type CareTemplateTab = 'essential' | 'enhanced' | 'premium'
export type CouponSubKey = 'center' | 'usage-log'

const SUB_ROUTE_NAMES: Record<LibrarySubKey, string> = {
  checklist: 'LibraryChecklistItems',
  templates: 'LibraryCareTemplates',
  'default-checklist': 'LibraryDefaultChecklist',
}

const ROUTE_NAME_TO_SUB: Partial<Record<string, LibrarySubKey>> = {
  LibraryChecklistItems: 'checklist',
  LibraryCareTemplates: 'templates',
  LibraryDefaultChecklist: 'default-checklist',
}

const COUPON_SUB_ROUTE_NAMES: Record<CouponSubKey, string> = {
  center: 'CouponCenter',
  'usage-log': 'CouponUsageLog',
}

const ROUTE_NAME_TO_COUPON_SUB: Partial<Record<string, CouponSubKey>> = {
  CouponCenter: 'center',
  CouponUsageLog: 'usage-log',
}

export function librarySubKeyFromRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): LibrarySubKey | null {
  const name = route.name ? String(route.name) : ''
  return ROUTE_NAME_TO_SUB[name] ?? null
}

export function libraryListRouteForSub(key: LibrarySubKey): RouteLocationNamedRaw {
  return { name: SUB_ROUTE_NAMES[key] }
}

export function techniciansRoute(): RouteLocationNamedRaw {
  return { name: 'Technicians' }
}

export function usersRoute(): RouteLocationNamedRaw {
  return { name: 'Users' }
}

export function usersDetailRoute(userId: string): RouteLocationNamedRaw {
  return { name: 'UserDetail', params: { userId: userId.trim() } }
}

export function isUsersRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'Users' || route.name === 'UserDetail'
}

export function incomeLogRoute(): RouteLocationNamedRaw {
  return { name: 'IncomeLog' }
}

export function isIncomeLogRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'IncomeLog'
}

export function careTemplatesRoute(tab?: CareTemplateTab): RouteLocationNamedRaw {
  if (!tab) return { name: 'LibraryCareTemplates' }
  return { name: 'LibraryCareTemplates', query: { tab } }
}

export function careTemplateTabFromRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name' | 'query'>,
): CareTemplateTab {
  if (route.name !== 'LibraryCareTemplates') return 'essential'
  const tab = typeof route.query.tab === 'string' ? route.query.tab : ''
  if (tab === 'enhanced' || tab === 'premium') return tab
  return 'essential'
}

export function isTechniciansRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'Technicians'
}

export function scheduleRoute(): RouteLocationNamedRaw {
  return { name: 'Schedule' }
}

export function isScheduleRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'Schedule'
}

export function tasksMapRoute(): RouteLocationNamedRaw {
  return { name: 'TasksMap' }
}

export function isTasksMapRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'TasksMap'
}

export function couponRouteForSub(key: CouponSubKey): RouteLocationNamedRaw {
  return { name: COUPON_SUB_ROUTE_NAMES[key] }
}

export function couponCenterRoute(): RouteLocationNamedRaw {
  return { name: 'CouponCenter' }
}

export function couponDetailRoute(couponId: string): RouteLocationNamedRaw {
  return { name: 'CouponDetail', params: { couponId: couponId.trim() } }
}

export function couponCopyRoute(couponId: string): RouteLocationNamedRaw {
  return { name: 'CouponCopy', params: { couponId: couponId.trim() } }
}

export function couponSubKeyFromRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): CouponSubKey | null {
  const name = route.name ? String(route.name) : ''
  return ROUTE_NAME_TO_COUPON_SUB[name] ?? null
}

export function isCouponRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return couponSubKeyFromRoute(route) !== null || route.name === 'CouponDetail' || route.name === 'CouponCopy'
}
